import {TagType} from "#generated"
import type {AbilityBuilder} from "@casl/ability"
import type {ColormassAbility} from "#auth/types"

export const conveyMembershipRights = (
    abilityBuilder: AbilityBuilder<ColormassAbility>,
    organizationId: string,
    isEnterpriseCloud: boolean,
    readonly: boolean,
) => {
    const {can: allow} = abilityBuilder
    const inOrganization = {"organization.id": organizationId}

    if (!readonly) {
        allow("copy", "Material", inOrganization)
        allow("copy", "Picture", inOrganization)
        allow("copy", "Template", inOrganization)

        allow("create", "DataObject", inOrganization)
        allow("create", "MaterialAssignment", {"asset.organization.id": organizationId})
        allow("create", "MaterialRevision", {"material.organization.id": organizationId})
        allow("create", "Tag")
        allow("create", "Task", inOrganization)

        allow("delete", "Asset", inOrganization)
        allow("delete", "Material", inOrganization)
        allow("delete", "MaterialAssignment", {"material.organization.id": organizationId})
        allow("delete", "Model", inOrganization)
        allow("delete", "Organization", {id: organizationId})
        allow("delete", "PictureRevision", {"picture.organization.id": organizationId})
        allow("delete", "Template", inOrganization)

        allow("update", "Asset", "articleId", inOrganization)
        allow("update", "Asset", "name", inOrganization)
        allow("update", "DataObject", inOrganization)
        allow("update", "Material", ["articleId", "description", "info"], inOrganization)
        allow("update", "Material", "info", inOrganization)
        allow("update", "MaterialRevision", {"material.organization.id": organizationId})
        allow("update", "Model", inOrganization)
        allow("update", "Organization", {id: organizationId})
        allow("update", "Picture", "pictureAnnotationTags", inOrganization)
        allow("update", "Project", inOrganization)
        allow("update", "Set", {"project.organization.id": organizationId})
        allow("update", "Task", "pins", inOrganization)
        allow("update", "Template", ["tag"], inOrganization)

        allow("update", ["Asset", "Material", "Model", "Template"], "galleryImage", inOrganization)
        allow("update", ["User"], "galleryImage", {memberships: inOrganization})
        allow("update", ["Asset", "DataObject", "Hdri", "Job", "JobTask", "Material", "Model", "Picture", "Template"], inOrganization)
        allow("update", "Tag", inOrganization)

        // hide these sections in the details views
        allow("read", "Material", ["files", "outputs", "templates"], inOrganization)
        allow("download", "Material", inOrganization)
        // Own-org members may download a template's outputs (GLTF/STL) and see its details sidebar. Public
        // (non-owner / anonymous) library viewers lack this, so the detail view offers them a notice instead.
        allow("download", "Template", inOrganization)
        allow("read", "Template", ["comment", "files", "additionalMenuItems", "templates", "transformDialog", "revisions"], inOrganization)
        allow("read", "Material", ["sampleArrival"])
        allow("read", "Asset", "files", inOrganization)
        allow("read", "Model", "files", inOrganization)
        allow("read", "Model", "productCategoryTags", inOrganization)

        allow("read", "Menu", ["renamePictureFile"])
    }

    allow("read", "Organization", {id: organizationId})
    allow("read", "Page", "tags")
    allow("read", "Picture", "state", inOrganization)
    allow("read", "Picture", "tasks", inOrganization)
    allow("read", "Picture", "pictureAnnotationTags", inOrganization)
    allow("read", "Menu", ["archiveTask", "tags"])

    if (isEnterpriseCloud) {
        if (!readonly) {
            allow("update", "Material", "state", inOrganization)
            allow("create", "MaterialRevision", {"material.organization.id": organizationId})
            allow("delete", "MaterialMapsExport")
            allow("update", "Menu", "debug")
            allow("update", "Picture", inOrganization)
            allow("update", "TextureSet", {"textureGroup.organization.id": organizationId})
            allow("create", "Template", inOrganization)
            allow("update", "Template", ["comment", "name", "3d"], inOrganization)
            allow("delete", "TemplateRevision", {"template.organization.id": organizationId})
            allow("update", "TemplateRevision", {"template.organization.id": organizationId})
            allow("update", "templateViewType", "pictures")
            allow("read", "Material", ["batchDownload", "explorerAdvanced", "revisions", "state", "textures"], inOrganization)
            allow("read", "Material", ["explorer", "labels", "rangeTags"])
        }

        allow("read", "Job", "tiling", inOrganization)
        allow("read", "TextureSet", {"textureGroup.organization.id": organizationId})
        allow("read", "Template", ["comment", "files"], inOrganization)

        allow("read", "MaterialRevision", {"material.organization.id": organizationId})
        allow("read", "Menu", ["batchDownloadMaterials", "batchStartMaterialImageJobs", "materialAi", "onlineLinks", "products", "scanning", "scenes", "tags"])
        allow("read", "Picture", "editor", inOrganization)
        allow("read", "ScanJob", {"textureSet.textureGroup.organization.id": organizationId})

        allow("read", "Page", ["products", "scenes", "tags"])

        allow("read", "Job", "tiling", inOrganization)
        allow("read", "Picture", "renderJobs")
    } else {
        allow("read", "Material", "assets")
        if (!readonly) {
            allow("update", "templateViewType")
        }
    }

    allow("read", "TagType", [TagType.MaterialRange, TagType.PictureAnnotation, TagType.ProductCategory, TagType.ProductRange])
}
